import { useMemo } from "react";

import { BTDropdown } from "@btnet/core-components";

import { BTAvatarDeprecated } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated";
import { type BTAvatarDeprecatedSize } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.types";
import { sizeNameToPixels } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.utils";
import { BTIconEllipsisOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTUser } from "commonComponents/btWrappers/BTUser/BTUser";
import { BTOverflowDeprecated } from "commonComponents/utilities/BTOverflowDeprecated/BTOverflowDeprecated";

import "./BTUserGroup.less";

interface IAvatarProps {
    displayName: string;
    globalUserId: number;
}

interface IBTUserGroupProps {
    data: IAvatarProps[];
    size?: BTAvatarDeprecatedSize;

    singleUserDisplayType?: "mini" | "full";
    /**
     * If provided, ensures this user appears first in the list (if present in `data`).
     * All other users will follow in alphabetical order.
     */
    featuredUserId?: number;
    boldCurrentUser?: boolean;
}

export const BTUserGroup: React.FunctionComponent<IBTUserGroupProps> = ({
    data,
    size = "middle",
    featuredUserId,
    singleUserDisplayType = "full",
    boldCurrentUser,
}) => {
    const orderedUsers = useMemo(() => {
        if (!featuredUserId) {
            return [...data].sort((a, b) =>
                a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()),
            );
        }

        return [
            ...data.filter((u) => u.globalUserId === featuredUserId),
            ...data
                .filter((u) => u.globalUserId !== featuredUserId)
                .sort((a, b) =>
                    a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()),
                ),
        ];
    }, [data, featuredUserId]);

    if (orderedUsers.length === 1) {
        return (
            <BTUser
                size={size}
                key={orderedUsers[0].globalUserId}
                displayName={orderedUsers[0].displayName}
                globalUserId={orderedUsers[0].globalUserId}
                type={singleUserDisplayType}
                boldCurrentUser={boldCurrentUser}
            />
        );
    }

    const overflowPixels = -sizeNameToPixels(size);

    const overlay = (omittedItems: IAvatarProps[]) => (
        <BTMenu
            testid="userGroupMenu"
            selectable={false}
            style={{
                marginLeft: overflowPixels,
                zIndex: orderedUsers.length + 1,
            }}
        >
            {orderedUsers.slice(orderedUsers.length - omittedItems.length - 1).map((item) => (
                <BTMenu.Item testid={`user-${item.globalUserId}`} key={item.globalUserId}>
                    <BTUser
                        key={item.globalUserId}
                        displayName={item.displayName}
                        globalUserId={item.globalUserId}
                        type="full"
                    />
                </BTMenu.Item>
            ))}
        </BTMenu>
    );

    return (
        <BTOverflowDeprecated
            className="BTUserGroup"
            // note: -8 for overlap
            itemWidth={sizeNameToPixels(size) - 10}
            style={{
                width: "100%",
            }}
            renderItem={(u) => (
                <BTUser
                    size={size}
                    key={u.globalUserId}
                    displayName={u.displayName}
                    globalUserId={u.globalUserId}
                    style={{
                        zIndex: orderedUsers.length - orderedUsers.indexOf(u),
                    }}
                />
            )}
            renderRest={(omittedItems) => (
                <BTDropdown
                    testid="user-group-overflow-dropdown"
                    destroyPopupOnHide
                    overlay={overlay(omittedItems)}
                >
                    <div role="button" tabIndex={0} onClick={(e) => e.stopPropagation()}>
                        <BTAvatarDeprecated
                            size={size}
                            className="BTAvatar-Overflow"
                            displayName={`+${omittedItems.length + 1}`}
                            icon={<BTIconEllipsisOutlined />}
                            style={{
                                marginLeft: overflowPixels,
                                zIndex: orderedUsers.length + 1,
                            }}
                            useInitials={false}
                        />
                    </div>
                </BTDropdown>
            )}
            data={orderedUsers}
        />
    );
};
